import { useState, useEffect, useRef } from 'react';
import { usersAPI, objectsAPI, getErrorMessage } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import RoleBadge from '@/components/RoleBadge';
import EmployeeNotes from '@/components/EmployeeNotes';
import { formatDateTime } from '@/lib/utils';
import { 
  Users, 
  Edit,
  Trash2,
  Loader2,
  Search,
  Building2,
  Upload,
  FileSpreadsheet,
  Download,
  MessageSquare,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

export default function Employees() {
  const { isAdmin } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [objects, setObjects] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedUserForNotes, setSelectedUserForNotes] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [csvContent, setCsvContent] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee',
    assigned_objects: [],
    is_active: true
  });

  const openNotesDialog = (user) => {
    setSelectedUserForNotes(user);
    setNotesDialogOpen(true);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, objectsRes] = await Promise.all([
        usersAPI.list(),
        objectsAPI.list()
      ]);
      setUsers(usersRes.data);
      setObjects(objectsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setShowPassword(false);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      assigned_objects: user.assigned_objects || [],
      is_active: user.is_active
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setShowPassword(false);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'employee',
      assigned_objects: [],
      is_active: true
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.name || !formData.name.trim()) {
      toast.error(t('employees.msg.nameRequired'));
      return;
    }
    
    if (!editingUser) {
      if (!formData.email || !formData.email.trim()) {
        toast.error(t('employees.msg.emailRequired'));
        return;
      }
    }
    
    if (editingUser && formData.password && (formData.password.length < 8 || !/[a-zA-Z]/.test(formData.password) || !/[0-9]/.test(formData.password))) {
      toast.error(t('employees.msg.passwordWeak'));
      return;
    }

    const effectiveRole = formData.role && formData.role.trim() ? formData.role.trim() : 'employee';
    
    try {
      if (editingUser) {
        const updateData = {
          name: formData.name.trim(),
          role: effectiveRole,
          assigned_objects: formData.assigned_objects,
          is_active: formData.is_active
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await usersAPI.update(editingUser.id, updateData);
        toast.success(t('employees.msg.updated'));
      } else {
        await usersAPI.create({
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: effectiveRole,
          assigned_objects: formData.assigned_objects
        });
        toast.success(t('employees.inviteSent') || 'Invitation sent to employee email');
      }
      setDialogOpen(false);
      setEditingUser(null);
      loadData();
    } catch (error) {
      console.error('Failed to save employee:', error);
      toast.error(getErrorMessage(error, t('msg.error'), t));
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t('employees.confirmDelete'))) return;
    
    try {
      await usersAPI.delete(userId);
      toast.success(t('employees.msg.deleted'));
      loadData();
    } catch (error) {
      console.error('Failed to delete employee:', error);
      toast.error(t('msg.error'));
    }
  };

  const toggleObjectAssignment = (objectId) => {
    const current = formData.assigned_objects;
    if (current.includes(objectId)) {
      setFormData({
        ...formData,
        assigned_objects: current.filter(id => id !== objectId)
      });
    } else {
      setFormData({
        ...formData,
        assigned_objects: [...current, objectId]
      });
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCsvContent(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleBulkImport = async () => {
    if (!csvContent.trim()) {
      toast.error(t('employees.import.noCsvData'));
      return;
    }

    setImportLoading(true);
    try {
      const lines = csvContent.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      // Check if required headers exist
      const hasName = headers.includes('name');
      const hasEmail = headers.includes('email');
      
      if (!hasName || !hasEmail) {
        toast.error(t('employees.import.missingHeaders'));
        setImportLoading(false);
        return;
      }
      
      const employees = [];
      const errors = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        if (values.length === 1 && values[0] === '') continue; // Skip empty lines
        
        const emp = {};
        headers.forEach((header, idx) => {
          if (header === 'name') emp.name = values[idx];
          if (header === 'email') emp.email = values[idx];
          if (header === 'password') emp.password = values[idx];
          if (header === 'role') emp.role = values[idx] || 'employee';
          if (header === 'assigned_objects' || header === 'objects') {
            emp.assigned_objects = values[idx] ? values[idx].split(';').map(o => o.trim()) : [];
          }
        });
        
        if (emp.name && emp.email) {
          employees.push({
            name: emp.name,
            email: emp.email,
            role: emp.role || 'employee',
            assigned_objects: emp.assigned_objects || []
          });
        } else {
          errors.push(t('employees.import.lineError', { line: i + 1 }));
        }
      }

      if (employees.length === 0) {
        toast.error(t('msg.noValidData'));
        return;
      }

      const result = await usersAPI.bulkImport(employees);
      
      toast.success(`${t('msg.importComplete')}: ${result.data.created} created, ${result.data.skipped} skipped`);
      
      if (result.data.errors?.length > 0) {
        console.error('Import errors:', result.data.errors);
      }

      setImportDialogOpen(false);
      setCsvContent('');
      loadData();
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(getErrorMessage(error, t('msg.importFailed'), t));
    } finally {
      setImportLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = 'name,email,role,assigned_objects\nJohn Doe,john@example.com,employee,\nJane Smith,jane@example.com,employee,';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employees_template.csv';
    a.click();
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-500" />
          <div className="absolute inset-0 blur-xl bg-wp-medium/30 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-white font-heading flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-wp-medium/20 flex items-center justify-center">
              <Users className="h-6 w-6 text-wp-light" />
            </div>
            {t('employees.title')}
          </h1>
          <p className="text-wp-light/40 mt-2">{t('employees.subtitle')}</p>
        </div>
        {isAdmin && (
          <div className="flex gap-3">
            <button 
              onClick={openCreateDialog}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-wp-medium hover:to-violet-500 text-white font-medium rounded-xl transition-all btn-glow"
              data-testid="add-employee-button"
            >
              <Users className="h-4 w-4" />
              {t('employees.addEmployee')}
            </button>
            <button 
              onClick={() => setImportDialogOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-wp-dark/30 hover:bg-wp-dark/50 text-white font-medium rounded-xl border border-wp-dark/50 transition-colors"
              data-testid="bulk-import-button"
            >
              <Upload className="h-4 w-4" />
              {t('employees.bulkImport')}
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
          <input
            placeholder={t('employees.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-wp-dark/30 border border-wp-dark/50 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-wp-medium/50"
            data-testid="employee-search-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px] bg-wp-dark/30 border-wp-dark/50 text-white">
            <SelectValue placeholder={t('common.allRoles')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.allRoles')}</SelectItem>
            <SelectItem value="admin">{t('role.admin')}</SelectItem>
            <SelectItem value="hr">{t('role.hr')}</SelectItem>
            <SelectItem value="controller">{t('role.controller')}</SelectItem>
            <SelectItem value="employee">{t('role.employee')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Employees Table */}
      <div className="glass-card rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-wp-dark/50 hover:bg-transparent">
                <TableHead className="text-wp-light/50">{t('common.name')}</TableHead>
                <TableHead className="text-wp-light/50">{t('common.email')}</TableHead>
                <TableHead className="text-wp-light/50">{t('common.role')}</TableHead>
                <TableHead className="text-wp-light/50">{t('employees.assignedObjects')}</TableHead>
                <TableHead className="text-wp-light/50">{t('common.status')}</TableHead>
                <TableHead className="text-wp-light/50">{t('common.created')}</TableHead>
                <TableHead className="text-right text-wp-light/50">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-wp-light/40">
                    {t('common.none')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} className="border-wp-dark/30 hover:bg-wp-dark/30" data-testid={`employee-row-${user.id}`}>
                    <TableCell className="font-medium text-white">{user.name}</TableCell>
                    <TableCell className="text-wp-light/50">{user.email}</TableCell>
                    <TableCell>
                      <RoleBadge role={user.role} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.assigned_objects?.length > 0 ? (
                          user.assigned_objects.map(objId => {
                            const obj = objects.find(o => o.id === objId);
                            return obj ? (
                              <span key={objId} className="text-xs px-2 py-0.5 bg-wp-dark/50 rounded-full text-wp-light/70">
                                {obj.name}
                              </span>
                            ) : null;
                          })
                        ) : (
                          <span className="text-white/30 text-sm">{t('common.none')}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs px-2.5 py-1 rounded-full ${user.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                        {user.is_active ? t('common.active') : t('common.inactive')}
                      </span>
                    </TableCell>
                    <TableCell className="text-wp-light/40 text-sm">
                      {formatDateTime(user.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => openNotesDialog(user)} 
                          className="p-2 rounded-lg bg-wp-medium/10 hover:bg-wp-medium/20 text-wp-light transition-colors"
                          data-testid={`notes-employee-${user.id}`}
                          title="Заметки"
                        >
                          <MessageSquare className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => openEditDialog(user)} 
                          className="p-2 rounded-lg bg-wp-dark/30 hover:bg-wp-dark/50 text-wp-light/50 hover:text-wp-lightest transition-colors"
                          data-testid={`edit-employee-${user.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(user.id)} 
                          className="p-2 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 transition-colors"
                          data-testid={`delete-employee-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-wp-darker border-wp-dark/50">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">
              {editingUser ? t('dialog.editEmployee') : t('employees.addEmployee')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-wp-light/70">{t('common.name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder={t('employees.namePlaceholder')}
                className="bg-wp-dark/30 border-wp-dark/50 text-white"
              />
            </div>

            {/* Email - only for new user */}
            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="email" className="text-wp-light/70">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  placeholder="name@company.com"
                  className="bg-wp-dark/30 border-wp-dark/50 text-white"
                />
              </div>
            )}

            {/* Password — only shown when editing existing employee */}
            {editingUser && (
            <div className="space-y-2">
              <Label htmlFor="password" className="text-wp-light/70">
                {t('employees.newPassword')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder={t('employees.leaveEmptyPassword')}
                  className="bg-wp-dark/30 border-wp-dark/50 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-wp-light/50 hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {formData.password && (
                <p className={`text-xs ${formData.password.length >= 8 && /[a-zA-Z]/.test(formData.password) && /[0-9]/.test(formData.password) ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {formData.password.length >= 8 && /[a-zA-Z]/.test(formData.password) && /[0-9]/.test(formData.password)
                    ? '✓ ' + t('employees.msg.passwordOk')
                    : t('employees.msg.passwordWeak')}
                </p>
              )}
            </div>
            )}

            {/* Invite hint when creating */}
            {!editingUser && (
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 px-4 py-3 text-sm text-emerald-300">
                ✉ {t('employees.inviteSent') || 'An invitation will be sent to the employee\'s email'}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="role" className="text-wp-light/70">{t('common.role')}</Label>
              <Select
                value={['admin','hr','controller','employee'].includes(formData.role) ? formData.role : 'custom'}
                onValueChange={(v) => {
                  if (v === 'custom') {
                    setFormData({...formData, role: '', assigned_objects: []});
                  } else {
                    setFormData({
                      ...formData,
                      role: v,
                      assigned_objects: v === 'employee' ? formData.assigned_objects : []
                    });
                  }
                }}
              >
                <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t('role.admin')}</SelectItem>
                  <SelectItem value="hr">{t('role.hr')}</SelectItem>
                  <SelectItem value="controller">{t('role.controller')}</SelectItem>
                  <SelectItem value="employee">{t('role.employee')}</SelectItem>
                  <SelectItem value="custom">{t('role.custom') || 'Custom role...'}</SelectItem>
                </SelectContent>
              </Select>
              {!['admin','hr','controller','employee'].includes(formData.role) && (
                <Input
                  value={formData.role}
                  onChange={e => setFormData({...formData, role: e.target.value})}
                  placeholder={t('role.customPlaceholder') || 'Enter role name...'}
                  className="bg-wp-dark/30 border-wp-dark/50 text-white mt-2"
                />
              )}
            </div>

            {formData.role === 'employee' ? (
              <div className="space-y-2">
                <Label className="text-wp-light/70">{t('employees.assignedObjects')}</Label>
                <div className="border border-wp-dark/50 rounded-xl p-3 space-y-2 max-h-48 overflow-y-auto bg-wp-dark/30">
                  {objects.length === 0 ? (
                    <p className="text-sm text-wp-light/40">{t('dialog.noObjectsAvailable')}</p>
                  ) : (
                    objects.map((obj) => (
                      <div key={obj.id} className="flex items-start space-x-2">
                        <Checkbox
                          id={`obj-${obj.id}`}
                          checked={formData.assigned_objects.includes(obj.id)}
                          onCheckedChange={() => toggleObjectAssignment(obj.id)}
                        />
                        <label htmlFor={`obj-${obj.id}`} className="text-sm flex items-center gap-2 cursor-pointer text-wp-light/70 break-words">
                          <Building2 className="h-4 w-4 text-wp-light/40 shrink-0" />
                          <span>{obj.name}</span>
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-wp-dark/30 bg-wp-dark/20 p-4 text-sm text-wp-light/50">
                {t('employees.objectsOnlyForEmployees') || 'Objects can only be assigned to employees'}
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({...formData, is_active: v})}
              />
              <label htmlFor="is_active" className="text-sm text-wp-light/70">{t('dialog.accountActive')}</label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button 
                type="button" 
                onClick={() => setDialogOpen(false)}
                className="px-5 py-2.5 bg-wp-dark/30 hover:bg-wp-dark/50 text-white rounded-xl border border-wp-dark/50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button 
                type="submit" 
                className="px-5 py-2.5 bg-wp-medium hover:bg-wp-medium text-white font-medium rounded-xl transition-all btn-glow"
              >
                {editingUser ? t('common.saveChanges') : t('common.create')}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-2xl bg-wp-darker border-wp-dark/50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white font-heading">
              <FileSpreadsheet className="h-5 w-5 text-wp-light" />
              {t('dialog.bulkImport')}
            </DialogTitle>
            <DialogDescription className="text-wp-light/50">
              {t('dialog.bulkImportDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Template download */}
            <div className="flex items-center justify-between p-4 bg-wp-dark/30 rounded-xl border border-wp-dark/50">
              <div>
                <p className="text-sm font-medium text-white">{t('dialog.downloadTemplate')}</p>
                <p className="text-xs text-wp-light/40">{t('dialog.getFormat')}</p>
              </div>
              <button 
                onClick={downloadTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-wp-dark/30 hover:bg-wp-dark/50 text-white rounded-lg border border-wp-dark/50 transition-colors"
              >
                <Download className="h-4 w-4" />
                {t('common.template')}
              </button>
            </div>

            {/* File upload */}
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('dialog.uploadFile')}</Label>
              <Input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="bg-wp-dark/30 border-wp-dark/50 text-white cursor-pointer file:bg-wp-dark/50 file:border-0 file:text-wp-light/70 file:mr-4"
              />
            </div>

            {/* Manual CSV input */}
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('dialog.pasteCSV')}</Label>
              <Textarea
                placeholder="name,email,password,role,assigned_objects&#10;John Doe,john@example.com,password123,employee,"
                value={csvContent}
                onChange={(e) => setCsvContent(e.target.value)}
                rows={8}
                className="font-mono text-sm bg-wp-dark/30 border-wp-dark/50 text-white"
              />
            </div>

            {/* Format help */}
            <div className="text-xs text-wp-light/40 p-4 bg-wp-dark/30 rounded-xl border border-wp-dark/50">
              <p className="font-medium mb-2 text-wp-light/60">{t('dialog.csvFormat')}</p>
              <p>• <strong className="text-wp-light/70">name</strong>: {t('dialog.nameRequired')}</p>
              <p>• <strong className="text-wp-light/70">email</strong>: {t('dialog.emailRequired')}</p>
              <p>• <strong className="text-wp-light/70">password</strong>: {t('dialog.passwordOptional')}</p>
              <p>• <strong className="text-wp-light/70">role</strong>: {t('dialog.roleOptional')}</p>
              <p>• <strong className="text-wp-light/70">assigned_objects</strong>: {t('dialog.objectsOptional')}</p>
            </div>

            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setImportDialogOpen(false)}
                className="px-5 py-2.5 bg-wp-dark/30 hover:bg-wp-dark/50 text-white rounded-xl border border-wp-dark/50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button 
                onClick={handleBulkImport} 
                disabled={importLoading || !csvContent.trim()}
                className="flex items-center gap-2 px-5 py-2.5 bg-wp-medium hover:bg-wp-medium text-white font-medium rounded-xl transition-all btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-import-button"
              >
                {importLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {t('common.import')}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="max-w-2xl bg-wp-darker border-wp-dark/50 flex flex-col max-h-[85vh]">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-wp-light" />
              {t('dialog.notesAbout')}
            </DialogTitle>
            <DialogDescription className="text-wp-light/50">
              {selectedUserForNotes?.name} ({selectedUserForNotes?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-1">
            {selectedUserForNotes && (
              <EmployeeNotes 
                employeeId={selectedUserForNotes.id}
                employeeName={selectedUserForNotes.name}
                isAdmin={isAdmin}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
